import { inject, Injectable } from '@angular/core';
import { CrcStatusType } from '@app/enums/CrcStatusType';
import { NoteStore } from '@components/molecules/forms/note-card/note.store';
import { AccessRequestDto, InvitationDto } from '@models/accessRequest';
import { Site, SiteNote, SiteSummaryViewModel } from '@models/site';
import { SiteContact } from '@models/siteContact';
import { User } from '@models/user';
import { AccessRequestService } from '@services/access-request/access-request.service';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { AdminStudyServices } from '@services/admin/admin-study.sevice';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    EMPTY,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    ReplaySubject,
    Subject,
    switchMap,
    take,
    tap,
} from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SiteStateService {
    constructor(
        private studyService: AdminStudyServices,
        private siteService: AdminSiteServices,
        private accessRequestService: AccessRequestService
    ) {
        this.siteLoadTrigger$
            .pipe(
                switchMap((id) =>
                    this.setSite$(id).pipe(
                        catchError((error) => {
                            console.error(`Unable to load site state for id ${id}`, error);
                            return EMPTY;
                        })
                    )
                )
            )
            .subscribe();
        this.siteAndUsersLoadTrigger$
            .pipe(
                switchMap((id) =>
                    this.setSiteAndStudyUsers$(id).pipe(
                        catchError((error) => {
                            console.error(`Unable to load site and users state for id ${id}`, error);
                            return EMPTY;
                        })
                    )
                )
            )
            .subscribe();
    }

    private readonly site = new BehaviorSubject<Site>(null);
    public readonly site$ = this.site.asObservable();

    private readonly sites = new BehaviorSubject<SiteSummaryViewModel[]>(null);
    public readonly sites$ = this.sites.asObservable();

    private readonly users = new ReplaySubject<User[]>();
    public readonly users$ = this.users.asObservable();

    private readonly siteContacts = new ReplaySubject<SiteContact[]>();
    public readonly siteContacts$ = this.siteContacts.asObservable();

    private readonly siteCountry = new BehaviorSubject<string | null>(null);
    private readonly siteLoadTrigger$ = new Subject<number>();
    private readonly siteAndUsersLoadTrigger$ = new Subject<number>();
    //old implementation used only in sites => should be removed during refactoring
    noteStore = inject(NoteStore);
    //new implementation for site notes that is using in request side notes and its migrated to signals
    public readonly siteNotes = new BehaviorSubject<SiteNote[]>([]);

    private getSiteAndCRCs(id: number) {
        return forkJoin({ site: this.siteService.getSite(id), crcs: this.siteService.getSiteCRCs(id) });
    }
    public setSite(id: number) {
        this.siteLoadTrigger$.next(id);
    }
    public setSite$(id: number) {
        return this.getSiteAndCRCs(id).pipe(
            take(1),
            tap((result) => {
                result.site.siteCRCs = result.crcs;
                this.siteNotes.next(result.site.siteNotes);
                this.noteStore.setSiteNotes(result.site.siteNotes);
                this.site.next(result.site);
                this.siteCountry.next(result.site.address.country);
            })
        );
    }
    public setSiteAndStudyUsers(id: number): void {
        this.siteAndUsersLoadTrigger$.next(id);
    }
    public setSiteAndStudyUsers$(id: number) {
        return this.getSiteAndCRCs(id).pipe(
            take(1),
            switchMap((result) => {
                const siteResult = result.site;
                siteResult.siteCRCs = result.crcs;
                this.siteNotes.next(result.site.siteNotes);
                this.noteStore.setSiteNotes(result.site.siteNotes);
                this.site.next(siteResult);
                return this.studyService.getStudyUsers(siteResult.studyId);
            }),
            tap((results) => {
                results.forEach((u) => {
                    u['displayName'] = `${u.firstName} ${u.lastName}`;
                });
                this.users.next(results);
            })
        );
    }

    public getSite(): Observable<Site> {
        return this.site$;
    }

    public setNewSite(newSite: Site): void {
        this.siteNotes.next([]);
        this.noteStore.setSiteNotes([]);
        this.site.next(newSite);
        this.siteCountry.next(null);
    }

    public setSiteCountry(country: string) {
        if (country !== this.siteCountry.getValue()) this.siteCountry.next(country);
    }

    public getSiteCountry(): Observable<string> {
        return this.siteCountry.asObservable();
    }

    public getSiteNotesFormData(): SiteNote[] {
        return this.noteStore.siteNotesFormGroup()
            ? this.noteStore.siteNotesFormGroup().getModelFromValue()
            : this.noteStore.siteNotes();
    }

    public clearStore() {
        this.site.next(null);
        this.siteCountry.next(null);
        this.siteNotes.next([]);
        this.noteStore.setSiteNotes([]);
    }

    public clearSites() {
        this.sites.next(null);
    }

    public setUsers(id: number): void {
        this.studyService
            .getStudyUsers(id)
            .pipe(take(1))
            .subscribe((res) => {
                res.forEach((u) => {
                    u['displayName'] = `${u.firstName} ${u.lastName}`;
                });
                return this.users.next(res);
            });
    }
    public setNewUsers(users: User[]) {
        this.users.next(users);
    }

    public getUsers(): Observable<User[]> {
        return this.users$;
    }

    public setSiteContacts() {
        combineLatest([this.site$, this.baseLogic$])
            .pipe(
                take(1),
                filter((site) => !!site),
                tap(([site, baseLogic]) => {
                    this.siteContacts.next(baseLogic);
                })
            )
            .subscribe();
    }

    public getSiteContacts(): Observable<SiteContact[]> {
        return this.siteContacts$;
    }

    public removeSiteContact(invitationGuid: string) {
        this.siteContacts.pipe(take(1)).subscribe((siteContacts) => {
            let filteredData = siteContacts.filter((x) => x.invitation.id !== invitationGuid);
            this.siteContacts.next(filteredData);
        });
    }

    invitedCrcs$: Observable<InvitationDto[]> = this.site$.pipe(
        filter((site) => !!site),
        switchMap((site) => this.accessRequestService.getInvitationsBySiteId(site.id))
    );

    requestedCrcs$: Observable<AccessRequestDto[]> = this.site$.pipe(
        filter((site) => !!site),
        switchMap((site) => combineLatest([of(site), this.accessRequestService.getAccessRequestsBySiteId(site.id)])),
        filter((ar) => !!ar),
        map(([site, accessRequests]) => accessRequests.filter((ar) => ar.affectedSiteIds?.includes(site.id)))
    );

    activeSiteCrcs$ = this.site$.pipe(
        filter((site) => !!site),
        switchMap((s) => this.siteService.getSiteCRCs(s.id))
    );

    baseLogic$: Observable<SiteContact[]> = combineLatest([
        this.invitedCrcs$,
        this.requestedCrcs$,
        this.activeSiteCrcs$,
    ]).pipe(
        map(([invitedCrcs, requestedCrcs, activeSiteCrcs]) => {
            let siteContacts: SiteContact[] = [];

            if (activeSiteCrcs.length > 0) {
                activeSiteCrcs.forEach((activeCrc) => {
                    siteContacts.push(<SiteContact>{
                        name: `${activeCrc.user.firstName} ${activeCrc.user.lastName}`,
                        emailAddress: activeCrc.user.emailAddress,
                        isPrimary: activeCrc.isPrimary,
                        status: CrcStatusType.Active,
                        siteCrc: activeCrc,
                    });
                });
            }

            if (requestedCrcs.length > 0) {
                requestedCrcs.forEach((requestedCrcs) => {
                    siteContacts.push(<SiteContact>{
                        name: `${requestedCrcs.firstName} ${requestedCrcs.lastName}`,
                        emailAddress: requestedCrcs.userEmail,
                        isPrimary: requestedCrcs.isPrimary,
                        status: CrcStatusType.RequestedApproval,
                        accessRequest: requestedCrcs,
                    });
                });
            }

            if (invitedCrcs.length > 0) {
                invitedCrcs.forEach((invitedCrc) => {
                    siteContacts.push(<SiteContact>{
                        name: `N/A`,
                        emailAddress: invitedCrc.userEmail,
                        isPrimary: invitedCrc.isPrimary,
                        status: CrcStatusType.Invited,
                        invitation: invitedCrc,
                    });
                });
            }
            siteContacts.sort((a, b) => (a.isPrimary ? -1 : 1));
            return siteContacts;
        })
    );

    public setSites(): void {
        this.siteService
            .getSitesVM()
            .pipe(
                take(1),
                tap((sites) => {
                    this.sites.next(sites);
                })
            )
            .subscribe();
    }

    public getSites(): Observable<SiteSummaryViewModel[]> {
        return this.sites$;
    }
}
